import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import {
  getTestScriptFiles,
  type TestScriptFilesPayload,
} from '@/components/smartai/api/getTestScriptFiles';
import {
  postTestScriptHashCheck,
  type TestScriptHashCheckData,
} from '@/components/smartai/api/getTestScriptHashCheck';
import {
  getTestcaseEntitiesCovered,
  isMethodOrFunctionEntity,
  type TestcaseCoveredEntity,
  type TestcaseEntitiesCoveredData,
} from '@/components/smartai/api/getTestcaseEntitiesCovered';
import {
  testCaseNameFromInventoryFilePath,
  type ScriptFileNode,
} from './scriptPathTree';
import ScriptInventoryList, {
  type ScriptCoverageSummary,
} from './ScriptInventoryList';
import {
  TestCasesPlaceholder,
  hashBannerClass,
  testCasesActionsClass,
  testCasesDetailAsideClass,
  testCasesDetailBodyClass,
  testCasesDetailHeaderClass,
  testCasesDetailNameClass,
  testCasesDetailPathClass,
  testCasesDetailTitleClass,
  testCasesEmptyMessageClass,
  testCasesEntityFileClass,
  testCasesEntityListClass,
  testCasesEntityRowClass,
  testCasesEntityTypeClass,
  testCasesErrorClass,
  testCasesHashBannerAddedClass,
  testCasesHashBannerChangedClass,
  testCasesHashBannerDismissClass,
  testCasesHashBannerFailedClass,
  testCasesHashBannerTextClass,
  testCasesHashCommitClass,
  testCasesHashDetailsClass,
  testCasesHashDetailsFailedClass,
  testCasesHashFailReasonClass,
  testCasesHashRecoveredClass,
  testCasesMainClass,
  testCasesPageLabelClass,
  testCasesPagerClass,
  testCasesRefreshingClass,
  testCasesScriptsCountClass,
  testCasesScriptsHeaderClass,
  testCasesScriptsScrollClass,
  testCasesScriptsTitleClass,
  testCasesTabRootClass,
  testCasesToolbarButtonClass,
  testCasesTreeWrapClass,
  type HashBannerVariant,
} from './test-cases-layout';
import { TestCasesEntitiesSkeleton, TestCasesTabSkeleton } from './TestCasesSkeleton';
import { cn } from '@/lib/utils';

type TestCasesTabProps = {
  scanId: string;
  /** MongoDB `repositories._id` for the code repo (`dbRepoId` from component selection). */
  codeRepositoryMongoId: string;

  /** PAT for ADO test repo (Requester-Token); may match code repo PAT when shared. */
  patToken?: string;

  /** Report total script count for the primary tab badge. */
  onTotalCount?: (total: number | undefined) => void;
};

const PAGE_SIZE = 30;

type CoveredEntityRow = TestcaseCoveredEntity & { filePath?: string };

function flattenEntities(
  data: TestcaseEntitiesCoveredData | null | undefined,
): CoveredEntityRow[] {
  const files = data?.files ?? [];
  const out: CoveredEntityRow[] = [];
  for (const f of files) {
    for (const ent of f.entities ?? []) {
      if (!isMethodOrFunctionEntity(ent.type)) continue;
      out.push({ ...ent, filePath: f.filePath });
    }
  }
  return out;
}

const TestCasesTab = ({
  scanId,
  codeRepositoryMongoId,
  patToken = '',
  onTotalCount,
}: TestCasesTabProps) => {
  const queryClient = useQueryClient();
  const trimmedScanId = scanId?.trim() ?? '';
  const trimmedId = codeRepositoryMongoId?.trim() ?? '';
  const trimmedPat = patToken?.trim() ?? '';
  const [page, setPage] = useState(1);
  const [selectedPathKey, setSelectedPathKey] = useState<string | null>(null);
  const [hashCheckLoading, setHashCheckLoading] = useState(false);
  const [hashCheckError, setHashCheckError] = useState<string | null>(null);
  const [hashCheckResult, setHashCheckResult] =
    useState<TestScriptHashCheckData | null>(null);
  const [coverageByPath, setCoverageByPath] = useState<
    Record<string, ScriptCoverageSummary>
  >({});
  const methodsPanelRef = useRef<HTMLElement>(null);

  const queryEnabled = !!trimmedId;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['test-script-files', trimmedId, page, PAGE_SIZE],
    queryFn: async (): Promise<TestScriptFilesPayload> => {
      const res = await getTestScriptFiles(trimmedId, {
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Failed to load test script files');
      }
      return res.data;
    },
    enabled: queryEnabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

  const selectedTestCaseName = useMemo(
    () =>
      selectedPathKey
        ? testCaseNameFromInventoryFilePath(selectedPathKey)
        : '',
    [selectedPathKey],
  );

  const selectedFileLabel = useMemo(() => {
    if (!selectedPathKey) return '';
    const parts = selectedPathKey.replace(/\\/g, '/').split('/').filter(Boolean);
    return parts[parts.length - 1] ?? selectedPathKey;
  }, [selectedPathKey]);

  const {
    data: entitiesRes,
    isLoading: entitiesLoading,
    isError: entitiesError,
    error: entitiesErr,
    refetch: refetchEntities,
  } = useQuery({
    queryKey: [
      'testcase-entities-covered',
      trimmedScanId,
      selectedTestCaseName,
    ],
    queryFn: async () => {
      const res = await getTestcaseEntitiesCovered(
        trimmedScanId,
        selectedTestCaseName,
      );
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Failed to load covered entities');
      }
      return res.data;
    },
    enabled: Boolean(trimmedScanId && selectedTestCaseName),
    staleTime: 30_000,
  });

  const totalCount = typeof data?.totalCount === 'number' ? data.totalCount : 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const canGoPrevious = page > 1;
  const canGoNext = page < totalPages;

  useEffect(() => {
    if (!queryEnabled) {
      onTotalCount?.(undefined);
      return;
    }
    if (data && typeof data.totalCount === 'number') {
      onTotalCount?.(data.totalCount);
    }
  }, [queryEnabled, data, onTotalCount]);

  useEffect(() => {
    setPage(1);
    setSelectedPathKey(null);
    setCoverageByPath({});
  }, [trimmedId]);

  useEffect(() => {
    setSelectedPathKey(null);
  }, [page]);

  useEffect(() => {
    if (typeof data?.totalCount !== 'number' || data.totalCount <= 0) return;
    const maxPage = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));
    if (page > maxPage) setPage(maxPage);
  }, [data?.totalCount, page]);

  const rows = data?.files ?? [];

  const onScriptFileClick = useCallback((node: ScriptFileNode) => {
    setSelectedPathKey(node.pathKey);
  }, []);

  const onHashCheck = useCallback(async () => {
    if (!trimmedId) return;
    if (!trimmedPat) {
      setHashCheckError(
        'PAT is missing. Provide a test repository PAT (or code repo PAT if shared).',
      );
      setHashCheckResult(null);
      return;
    }
    setHashCheckLoading(true);
    setHashCheckError(null);
    setHashCheckResult(null);
    try {
      const res = await postTestScriptHashCheck(trimmedId, trimmedPat);
      if (!res.success || !res.data) {
        throw new Error(res.message || 'Hash check failed');
      }
      setHashCheckResult(res.data);
      void queryClient.invalidateQueries({
        queryKey: ['test-script-files', trimmedId],
      });
      if (typeof res.data.summary?.dbTotalAfter === 'number') {
        onTotalCount?.(res.data.summary.dbTotalAfter);
      }
    } catch (e) {
      setHashCheckError(e instanceof Error ? e.message : String(e));
    } finally {
      setHashCheckLoading(false);
    }
  }, [trimmedId, trimmedPat, queryClient, onTotalCount]);

  const coveredEntities = useMemo(
    () => flattenEntities(entitiesRes),
    [entitiesRes],
  );

  useEffect(() => {
    if (!selectedPathKey || entitiesLoading || entitiesError) return;
    const total = coveredEntities.length;
    const hit = coveredEntities.filter(e => (e.hitLinesCount ?? 0) > 0).length;
    const pct = total > 0 ? Math.round((hit / total) * 100) : 0;
    setCoverageByPath(prev => ({
      ...prev,
      [selectedPathKey]: { pct, hit, total },
    }));
  }, [
    selectedPathKey,
    entitiesLoading,
    entitiesError,
    coveredEntities,
  ]);

  useEffect(() => {
    if (!selectedPathKey || entitiesLoading) return;
    methodsPanelRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [selectedPathKey, entitiesLoading, entitiesRes]);

  const entitiesErrorMessage = useMemo(() => {
    if (!entitiesError || !entitiesErr) return undefined;
    return entitiesErr instanceof Error
      ? entitiesErr.message
      : String(entitiesErr);
  }, [entitiesError, entitiesErr]);

  const errorMessage = useMemo(() => {
    if (!isError || !error) return undefined;
    return error instanceof Error ? error.message : String(error);
  }, [isError, error]);

  if (!trimmedId) {
    return (
      <TestCasesPlaceholder inner>
        <h2>Test Cases</h2>
        <p>
          Code repository id is missing for this component. Select a component
          from the list (with a valid Mongo repository id) and open Test Scripts Inventory
          from the sidebar.
        </p>
      </TestCasesPlaceholder>
    );
  }

  if (!trimmedScanId) {
    return (
      <TestCasesPlaceholder inner>
        <h2>Test Cases</h2>
        <p>Scan id is missing. Complete onboarding first.</p>
      </TestCasesPlaceholder>
    );
  }

  if (isLoading) {
    return <TestCasesTabSkeleton />;
  }

  if (isError && errorMessage) {
    return (
      <TestCasesPlaceholder inner>
        <h2>Test Cases</h2>
        <p className={testCasesErrorClass}>{errorMessage}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={testCasesToolbarButtonClass}
          onClick={() => void refetch()}>
          Retry
        </Button>
      </TestCasesPlaceholder>
    );
  }

  const hashBannerVariant: HashBannerVariant = hashCheckError
    ? 'error'
    : hashCheckResult?.hashCheckSkipped
      ? 'info'
      : (hashCheckResult?.fetchErrors ?? 0) > 0 ||
          (hashCheckResult?.summary?.fetchFailed ?? 0) > 0
        ? 'warn'
        : 'default';

  return (
    <div className={testCasesTabRootClass}>
      {(hashCheckError || hashCheckResult) && (
        <div
          className={hashBannerClass(hashBannerVariant)}
          role="status">
          {hashCheckError ? (
            <p className={testCasesHashBannerTextClass}>
              {hashCheckError}
            </p>
          ) : hashCheckResult?.summary ? (
            <>
              <p className={testCasesHashBannerTextClass}>
                {hashCheckResult.hashCheckSkipped ? (
                  <>
                    <strong>Up to date.</strong> Scripts match latest pipeline
                    commit
                    {hashCheckResult.resolvedCommitId && (
                      <>
                        {' '}
                        <code className={testCasesHashCommitClass}>
                          {hashCheckResult.resolvedCommitId.slice(0, 8)}…
                        </code>
                      </>
                    )}
                    {hashCheckResult.resolvedBuildId != null && (
                      <> (build {hashCheckResult.resolvedBuildId})</>
                    )}
                    . {hashCheckResult.summary.unchanged ?? 0} file
                    {(hashCheckResult.summary.unchanged ?? 0) === 1
                      ? ''
                      : 's'}{' '}
                    in inventory — no re-hash needed.
                  </>
                ) : (
                  <>
                    <strong>Hash check complete.</strong>{' '}
                    {hashCheckResult.resolvedBuildId != null && (
                      <>
                        Build {hashCheckResult.resolvedBuildId}
                        {hashCheckResult.resolvedCommitId && (
                          <>
                            {' '}
                            · commit{' '}
                            <code className={testCasesHashCommitClass}>
                              {hashCheckResult.resolvedCommitId.slice(0, 8)}…
                            </code>
                          </>
                        )}
                        {' · '}
                      </>
                    )}
                  </>
                )}
                {!hashCheckResult.hashCheckSkipped && (
                  <>
                    {hashCheckResult.summary.unchanged ?? 0} unchanged ·{' '}
                    <span className={testCasesHashBannerChangedClass}>
                      {hashCheckResult.summary.changed ?? 0} updated in DB
                    </span>
                    {' · '}
                    <span className={testCasesHashBannerAddedClass}>
                      {hashCheckResult.summary.added ?? 0} added
                    </span>
                    {(hashCheckResult.summary.removed ?? 0) > 0 && (
                      <>
                        {' · '}
                        {hashCheckResult.summary.removed} marked removed in DB
                      </>
                    )}
                    {(hashCheckResult.summary.restored ?? 0) > 0 && (
                      <>
                        {' · '}
                        {hashCheckResult.summary.restored} restored
                      </>
                    )}
                    {(hashCheckResult.summary.fetchFailed ?? 0) > 0 && (
                      <>
                        {' · '}
                        <span className={testCasesHashBannerFailedClass}>
                          {hashCheckResult.summary.fetchFailed} fetch failed (DB
                          not updated)
                        </span>
                      </>
                    )}
                  </>
                )}
              </p>
              {!hashCheckResult.hashCheckSkipped &&
                (hashCheckResult.changed?.length ?? 0) > 0 && (
                  <details className={testCasesHashDetailsClass}>
                    <summary>
                      Updated scripts ({hashCheckResult.changed?.length})
                    </summary>
                    <ul>
                      {hashCheckResult.changed?.map(row => (
                        <li key={row.filePath}>
                          <code>{row.filePath}</code>
                          {row.recoveredFromEmptyHash && (
                            <span className={testCasesHashRecoveredClass}>
                              {' '}
                              (repaired prior failed fetch)
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              {!hashCheckResult.hashCheckSkipped &&
                (hashCheckResult.fetchFailed?.length ?? 0) > 0 && (
                  <details
                    className={cn(
                      testCasesHashDetailsClass,
                      testCasesHashDetailsFailedClass,
                    )}>
                    <summary>
                      Fetch failed — not updated (
                      {hashCheckResult.fetchFailed?.length})
                    </summary>
                    <ul>
                      {hashCheckResult.fetchFailed?.map(row => (
                        <li key={row.filePath}>
                          <code>{row.filePath}</code>
                          {row.reason && (
                            <span className={testCasesHashFailReasonClass}>
                              {' '}
                              ({row.reason})
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              {!hashCheckResult.hashCheckSkipped &&
                (hashCheckResult.added?.length ?? 0) > 0 && (
                  <details className={testCasesHashDetailsClass}>
                    <summary>
                      New scripts ({hashCheckResult.added?.length})
                    </summary>
                    <ul>
                      {hashCheckResult.added?.map(row => (
                        <li key={row.filePath}>
                          <code>{row.filePath}</code>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              {!hashCheckResult.hashCheckSkipped &&
                (hashCheckResult.removed?.length ?? 0) > 0 && (
                  <details className={testCasesHashDetailsClass}>
                    <summary>
                      Removed scripts ({hashCheckResult.removed?.length})
                    </summary>
                    <ul>
                      {hashCheckResult.removed?.map(row => (
                        <li key={row.filePath}>
                          <code>{row.filePath}</code>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
            </>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            className={testCasesHashBannerDismissClass}
            aria-label="Dismiss hash check result"
            onClick={() => {
              setHashCheckError(null);
              setHashCheckResult(null);
            }}>
            ×
          </Button>
        </div>
      )}

      <div className={testCasesMainClass}>
        <div className={testCasesTreeWrapClass}>
          <div className={testCasesScriptsHeaderClass}>
            <h2 className={testCasesScriptsTitleClass}>
              Automation Scripts
              <span className={testCasesScriptsCountClass}>{totalCount}</span>
            </h2>
            <div className={testCasesActionsClass}>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={testCasesToolbarButtonClass}
                disabled={hashCheckLoading || !trimmedId}
                onClick={() => void onHashCheck()}>
                <RefreshCw
                  className={cn('h-4 w-4', hashCheckLoading && 'animate-spin')}
                  aria-hidden
                />
                {hashCheckLoading ? 'Checking…' : 'Hash Check'}
              </Button>
              <div className={testCasesPagerClass}>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={testCasesToolbarButtonClass}
                  disabled={!canGoPrevious}
                  onClick={() => setPage(p => Math.max(1, p - 1))}>
                  Previous
                </Button>
                <span className={testCasesPageLabelClass}>
                  Page {page} of {totalPages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={testCasesToolbarButtonClass}
                  disabled={!canGoNext}
                  onClick={() => setPage(p => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          </div>

          {isFetching && (
            <div className={testCasesRefreshingClass} aria-live="polite">
              Updating…
            </div>
          )}

          {rows.length === 0 ? (
            <p className={testCasesEmptyMessageClass}>
              No indexed test scripts for this inventory.
            </p>
          ) : (
            <div className={testCasesScriptsScrollClass}>
              <ScriptInventoryList
                files={rows}
                repoName={data?.repoName}
                selectedPathKey={selectedPathKey}
                coverageByPath={coverageByPath}
                onSelect={onScriptFileClick}
              />
            </div>
          )}

          {selectedPathKey && (
            <section
              ref={methodsPanelRef}
              className={testCasesDetailAsideClass}>
              <div className={testCasesDetailHeaderClass}>
                <p className={testCasesDetailTitleClass}>Methods &amp; Functions</p>
                <p className={testCasesDetailNameClass}>{selectedFileLabel}</p>
                <p className={testCasesDetailPathClass}>
                  <code>{selectedPathKey}</code>
                </p>
              </div>
              <div className={testCasesDetailBodyClass}>
                {entitiesLoading ? (
                  <TestCasesEntitiesSkeleton />
                ) : entitiesErrorMessage ? (
                  <>
                    <p className={testCasesErrorClass}>
                      {entitiesErrorMessage}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={testCasesToolbarButtonClass}
                      onClick={() => void refetchEntities()}>
                      Retry
                    </Button>
                  </>
                ) : entitiesRes?.error ? (
                  <p className={testCasesErrorClass}>{entitiesRes.error}</p>
                ) : coveredEntities.length === 0 ? (
                  <p className="text-[var(--inv-text-muted)]">
                    No methods or functions covered for this script file in the
                    current scan.
                  </p>
                ) : (
                  <>
                    <p className="mb-3 mt-0 text-[0.8125rem] font-semibold text-[var(--inv-text-secondary)]">
                      {coveredEntities.length} method
                      {coveredEntities.length === 1 ? '' : 's'} covered
                    </p>
                    <ul className={testCasesEntityListClass}>
                      {coveredEntities.map((ent, idx) => (
                        <li
                          key={`${ent.entityId ?? ent.entityKey ?? ent.name ?? idx}`}
                          className={testCasesEntityRowClass}>
                          <strong className="text-[var(--inv-text)]">
                            {ent.name ?? '—'}
                          </strong>
                          {ent.type && (
                            <span className={testCasesEntityTypeClass}>
                              {ent.type}
                            </span>
                          )}
                          {ent.filePath && (
                            <span
                              className={testCasesEntityFileClass}
                              title={ent.filePath}>
                              {ent.filePath.split('/').pop() ?? ent.filePath}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestCasesTab;
